import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInRouterContext, useLocation, useNavigate } from "react-router-dom";
import { Icons } from "./icons.jsx";
import SidebarTreeInline from "./SidebarTreeInline.jsx";

const DEFAULT_MENU = [
  {
    label: "Module Manager",
    icon: "IconFolder",
    path: "/module-manager",
    children: [
      { label: "Sidebar", icon: "IconList", path: "/module-manager/Sidebar" },
      { label: "Modules", icon: "IconDatabase", path: "/module-manager/Modules" },
      { label: "Modules description", icon: "IconList", path: "/module-manager/Modules-Description" }
    ]
  },
  { label: "Agents", icon: "IconUsers", path: "/agents" },
  { label: "Tools", icon: "IconTerminal", path: "/tools" },
  { label: "Logs", icon: "IconActivity", path: "/logs2" }
];

function initials(input = "") {
  const text = String(input || "").trim();
  if (!text) return "LC";
  const parts = text.split(/\s+/).slice(0, 2);
  if (parts.length === 1 && text.includes("@")) {
    return text[0] ? text[0].toUpperCase() : "LC";
  }
  const letters = parts
    .map((part) => (part ? part[0].toUpperCase() : ""))
    .join("");
  return letters || (text[0] ? text[0].toUpperCase() : "LC");
}

function normalizePath(raw) {
  const cleaned = String(raw || "")
    .trim()
    .replace(/[#?].*$/, "")
    .replace(/\/+$/, "");
  return cleaned || "/";
}

function getHashPath() {
  try {
    const hash = typeof window !== "undefined" ? String(window.location.hash || "") : "";
    if (!hash) return "/";
    const raw = hash.startsWith("#") ? hash.slice(1) : hash;
    const normalized = raw.startsWith("/") ? raw : `/${raw}`;
    return normalizePath(normalized);
  } catch {
    return "/";
  }
}

function isRouteMatch(path, current) {
  if (!path) return false;
  const p = normalizePath(path);
  const c = normalizePath(current);
  return c === p || c.startsWith(p + "/");
}

function resolveIcon(icon) {
  if (!icon) return null;
  if (typeof icon === "function") return icon;
  if (typeof icon === "string") {
    if (Icons && Icons[icon]) return Icons[icon];
    if (/^[\uD800-\uDBFF][\uDC00-\uDFFF]/.test(icon)) {
      return () => <span className="text-base" aria-hidden>{icon}</span>;
    }
  }
  return null;
}

function Chevron({ open }) {
  return (
    <svg
      viewBox="0 0 20 20"
      className={`h-4 w-4 transition-transform ${open ? "rotate-90" : ""}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M7 4l6 6-6 6" />
    </svg>
  );
}

const MenuNode = React.memo(function MenuNode({ node, level, onNavigate }) {
  const IconComp = resolveIcon(node.icon) || Icons.IconFolder;
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const isOpen = !!hasChildren && !!node.isOpen;
  const isCurrent = !!node.isCurrent;

  const handleClick = useCallback(() => {
    let target = node.path || "";
    if (!target && hasChildren) {
      const first = node.children.find((c) => !!c.path) || null;
      target = first ? first.path : "";
    }
    if (target) onNavigate(target, node);
  }, [node, hasChildren, onNavigate]);

  return (
    <li className="app-sidebar__item-row">
      <button
        type="button"
        className={`app-sidebar__item w-full ${isCurrent ? "is-active" : ""}`}
        onClick={handleClick}
        aria-current={node.isActive ? "page" : undefined}
        aria-expanded={hasChildren ? isOpen : undefined}
      >
        <span className="flex items-center gap-2 min-w-0">
          {IconComp ? <IconComp /> : null}
          <span className="truncate">{node.label}</span>
        </span>
        {hasChildren ? <Chevron open={isOpen} /> : null}
      </button>
      {hasChildren && isOpen ? (
        <ul className={`app-sidebar__tree level-${level + 1}`}>
          {node.children.map((child) => (
            <MenuNode key={child.key} node={child} level={level + 1} onNavigate={onNavigate} />
          ))}
        </ul>
      ) : null}
    </li>
  );
});

function SidebarShell({ currentPath, onNavigate, setActiveTab, me, onLogout, onGoProfile, menu, hasSidebar }) {
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const baseWidthRef = useRef(null);

  useEffect(() => {
    try {
      if (typeof document === "undefined") return;
      const root = document.documentElement;
      if (!root) return;
      if (!baseWidthRef.current) {
        const computed = getComputedStyle(root).getPropertyValue("--sidebar-width").trim();
        baseWidthRef.current = computed || "11.5rem";
      }
      const expandedWidth =
        getComputedStyle(root).getPropertyValue("--sidebar-width-expanded").trim() || "15rem";
      root.style.setProperty("--sidebar-width", expanded ? expandedWidth : baseWidthRef.current);
      return () => {
        root.style.setProperty("--sidebar-width", baseWidthRef.current || "11.5rem");
      };
    } catch {}
  }, [expanded]);

  const useDbMenu = !Array.isArray(menu) || menu.length === 0;
  const menuConfig = useMemo(() => (useDbMenu ? [] : menu), [useDbMenu, menu]);

  const buildTree = useCallback(
    (items) => {
      return items.map((item, idx) => {
        const key = item.key || item.path || `${item.label || "item"}-${idx}`;
        const children = Array.isArray(item.children) ? buildTree(item.children) : [];
        const activeSelf = isRouteMatch(item.path, currentPath);
        const activeChild = children.some((c) => c.isCurrent || c.isOpen);
        const isOpen = children.length > 0 && (activeSelf || activeChild);
        const isCurrent = activeSelf || activeChild;
        return {
          ...item,
          key,
          children,
          isActive: activeSelf,
          isOpen,
          isCurrent
        };
      });
    },
    [currentPath]
  );

  const tree = useMemo(() => buildTree(menuConfig), [buildTree, menuConfig]);

  const handleNavigate = useCallback(
    (path, node) => {
      if (typeof setActiveTab === "function") {
        const tab = node && node.tab ? node.tab : "modules";
        setActiveTab(tab);
      }
      onNavigate(path, node);
    },
    [onNavigate, setActiveTab]
  );

  useEffect(() => {
    const handleClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  return (
    <>
      <aside className={`app-sidebar ${expanded ? "app-sidebar--expanded" : ""}`}>
        <div className="app-sidebar__brand">
          <div className="app-sidebar__logo">LC</div>
          <span className="app-sidebar__brand-text">Livechat</span>
          <button
            type="button"
            className="app-sidebar__size-toggle"
            onClick={() => setExpanded((v) => !v)}
            title={expanded ? "Réduire le menu" : "Étendre le menu"}
          >
            {expanded ? "Réduire" : "Étendre"}
          </button>
        </div>

        <nav className="app-sidebar__nav">
          {useDbMenu ? (
            <>
              <SidebarTreeInline parent={null} level={0} className="" setActiveTab={setActiveTab} me={me} />
              {hasSidebar === false && (
                <div className="px-3 py-2 text-xs text-slate-500">
                  Aucun menu configuré. Ouvrez le Module Manager pour ajouter des entrées.
                </div>
              )}
            </>
          ) : (
            <ul className="app-sidebar__tree level-0">
              {tree.map((node) => (
                <MenuNode key={node.key} node={node} level={0} onNavigate={handleNavigate} />
              ))}
            </ul>
          )}
        </nav>

        <div className="app-sidebar__profile" ref={menuRef}>
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            className="app-sidebar__profile-btn"
            title={me?.name || me?.email || "Profile"}
            aria-label="Profile menu"
          >
            {me ? initials(me.name || me.email) : "LC"}
          </button>
          {open && (
            <div className="app-sidebar__dropdown">
              <div>
                <div className="font-semibold leading-5">{me?.name || me?.email || "Profile"}</div>
                {me?.email && <div className="mt-1 text-xs text-slate-500 break-all">{me.email}</div>}
              </div>
              <button
                onClick={() => {
                  setOpen(false);
                  if (typeof setActiveTab === "function") setActiveTab("modules");
                  onGoProfile?.();
                }}
              >
                Profile
              </button>
              <button
                onClick={() => {
                  setOpen(false);
                  if (typeof setActiveTab === "function") setActiveTab("modules");
                  onGoProfile?.();
                }}
              >
                Notifications
              </button>
              <button className="danger" onClick={() => { setOpen(false); onLogout?.(); }}>
                Sign out
              </button>
            </div>
          )}
        </div>
      </aside>
      {open && <div className="app-sidebar__overlay" onClick={() => setOpen(false)} aria-hidden="true" />}
    </>
  );
}

function SidebarRouter(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = useMemo(() => {
    const fromRouter = normalizePath(location?.pathname || "/");
    if (fromRouter && fromRouter !== "/") return fromRouter;
    return getHashPath();
  }, [location?.pathname, location?.search]);

  const onNavigate = useCallback(
    (path) => {
      const next = normalizePath(path);
      navigate(next);
    },
    [navigate]
  );

  return <SidebarShell {...props} currentPath={currentPath} onNavigate={onNavigate} />;
}

function SidebarHash(props) {
  const [currentPath, setCurrentPath] = useState(() => getHashPath());

  useEffect(() => {
    const onHash = () => setCurrentPath(getHashPath());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const onNavigate = useCallback((path) => {
    const next = normalizePath(path);
    const hash = next.startsWith("/") ? next : `/${next}`;
    if (typeof window !== "undefined") {
      window.location.hash = `#${hash}`;
    }
  }, []);

  return <SidebarShell {...props} currentPath={currentPath} onNavigate={onNavigate} />;
}

export default function Sidebar(props) {
  const inRouter = useInRouterContext();
  if (inRouter) return <SidebarRouter {...props} />;
  return <SidebarHash {...props} />;
}
